import { ArrowRight, Clock, Layers, RotateCcw, ServerCog } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { categoryLabel } from '@/modules/automation/status';
import {
  catalogPlaybook,
  catalogTemplate,
  type CatalogPlaybook,
  type CatalogTemplate,
  type DocPage,
} from '@/modules/docs/content';
import { Markdown } from '@/modules/docs/Markdown';
import type { AutomationParameter, PlaybookSummary } from '@/types/api';

/**
 * One documentation page, the same in the dashboard and on the public site.
 *
 * The facts at the top and the options table come from the exported catalogue; the prose comes from
 * the page's Markdown. `linkFor` points internal links at the reader's side of the site, and
 * `action` is what the header offers: "Open in Playbooks" inside the dashboard, nothing publicly.
 */
export function DocView({
  page,
  linkFor,
  action,
}: {
  page: DocPage;
  linkFor: (href: string) => string;
  action?: ReactNode;
}) {
  const playbook = page.kind === 'playbook' ? catalogPlaybook(page.slug) : undefined;
  const template = page.kind === 'template' ? catalogTemplate(page.slug) : undefined;

  return (
    <article className="min-w-0">
      <header className="border-border/60 border-b pb-6">
        <p className="text-primary text-xs font-semibold tracking-wider uppercase">
          {page.kind === 'playbook' ? 'Playbook' : page.kind === 'template' ? 'Template' : 'Troubleshooting'}
        </p>
        <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="text-foreground text-2xl font-semibold tracking-tight sm:text-3xl">{page.title}</h1>
            <p className="text-muted-foreground mt-2 max-w-3xl leading-relaxed">{page.summary}</p>
          </div>
          {action !== undefined && <div className="flex shrink-0 items-center gap-2">{action}</div>}
        </div>

        {playbook !== undefined && <PlaybookFacts playbook={playbook} />}
        {template !== undefined && <TemplateFacts template={template} linkFor={linkFor} />}
      </header>

      {(playbook?.parameters.length ?? template?.parameters.length ?? 0) > 0 && (
        <section className="border-border/60 border-b py-6">
          <h2 className="text-foreground text-lg font-semibold tracking-tight">Options</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            As they appear on the run form, with their defaults.
          </p>
          <OptionsTable parameters={playbook?.parameters ?? template?.parameters ?? []} />
        </section>
      )}

      <div className="pt-6">
        <Markdown source={page.body} linkFor={linkFor} />
      </div>
    </article>
  );
}

function Fact({ icon: Icon, label, children }: { icon: typeof Clock; label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-start gap-2.5">
      <Icon className="text-muted-foreground mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="min-w-0">
        <p className="text-muted-foreground text-xs">{label}</p>
        <p className="text-foreground text-sm">{children}</p>
      </div>
    </div>
  );
}

function minutes(seconds: number): string {
  const value = Math.round(seconds / 60);

  return `${value} minute${value === 1 ? '' : 's'}`;
}

function PlaybookFacts({ playbook }: { playbook: CatalogPlaybook }) {
  const category = categoryLabel[playbook.category as PlaybookSummary['category']] ?? playbook.category;

  return (
    <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Fact icon={Layers} label="Category">
        {category}
      </Fact>
      <Fact icon={Clock} label="Time limit per server">
        {minutes(playbook.timeout_seconds)}
      </Fact>
      <Fact icon={RotateCcw} label="Temporary failures">
        {playbook.retries > 0
          ? `Retried automatically, up to ${playbook.retries} time${playbook.retries === 1 ? '' : 's'}`
          : 'Not retried: run it again'}
      </Fact>
      <Fact icon={ServerCog} label="Version">
        {playbook.version}
      </Fact>
      <div className="sm:col-span-2 lg:col-span-4">
        <p className="text-muted-foreground text-xs">Supported systems</p>
        <p className="text-foreground mt-0.5 text-sm">{playbook.supported_systems}</p>
      </div>
    </div>
  );
}

function TemplateFacts({ template, linkFor }: { template: CatalogTemplate; linkFor: (href: string) => string }) {
  return (
    <div className="mt-6">
      <p className="text-muted-foreground text-xs">Steps, in order · version {template.version}</p>
      <ol className="mt-2 flex flex-wrap items-center gap-2">
        {template.steps.map((step, index) => (
          <li key={`${step.playbook}-${index}`} className="flex items-center gap-2">
            {index > 0 && <ArrowRight className="text-muted-foreground size-3.5" aria-hidden />}
            <Link
              to={linkFor(`/docs/playbooks/${step.playbook}`)}
              className="border-border hover:border-primary/40 hover:bg-muted/40 rounded-md border px-2.5 py-1 text-sm transition-colors"
            >
              {step.playbook_name}
              {step.optional && <span className="text-muted-foreground"> (optional)</span>}
            </Link>
          </li>
        ))}
      </ol>
      <div className="mt-4">
        <p className="text-muted-foreground text-xs">Supported systems</p>
        <p className="text-foreground mt-0.5 text-sm">{template.supported_systems}</p>
      </div>
    </div>
  );
}

const TYPE_LABELS: Record<AutomationParameter['type'], string> = {
  choice: 'One choice',
  multi_choice: 'Any of the choices',
  boolean: 'On or off',
  text: 'Text',
  list: 'List: one per line or comma-separated',
  lines: 'One per line',
};

function defaultText(parameter: AutomationParameter): string {
  const value = parameter.default;

  if (typeof value === 'boolean') {
    return value ? 'On' : 'Off';
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return parameter.required ? 'Required' : 'None';
    }

    return value
      .map((item) => parameter.options.find((option) => option.value === item)?.label ?? item)
      .join(', ');
  }

  if (value === '') {
    return parameter.required ? 'Required' : 'Empty';
  }

  return parameter.options.find((option) => option.value === value)?.label ?? value;
}

export function OptionsTable({ parameters }: { parameters: AutomationParameter[] }) {
  return (
    <div className="mt-4 space-y-3">
      {parameters.map((parameter) => (
        <div
          key={parameter.key}
          className="border-border/60 bg-muted/20 grid gap-3 rounded-lg border p-4 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] sm:gap-6"
        >
          <div className="min-w-0">
            <p className="text-foreground font-medium">{parameter.label}</p>
            <p className="text-muted-foreground mt-1 text-xs">{TYPE_LABELS[parameter.type]}</p>
          </div>
          <div className="min-w-0 space-y-2 text-sm">
            {parameter.help !== '' && <p className="text-muted-foreground">{parameter.help}</p>}
            {parameter.options.length > 0 && (
              <ul className="flex flex-wrap gap-1.5">
                {parameter.options.map((option) => (
                  <li key={option.value} className="border-border bg-background rounded border px-2 py-0.5 text-xs">
                    {option.label}
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs">
              <span className="text-muted-foreground">Default: </span>
              <span className="text-foreground">{defaultText(parameter)}</span>
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
